import { Component, Inject, inject } from '@angular/core';
import { UserService } from '../../../../shared/services/user.service';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  AuthDialogMethod,
  AUTHORIZATION_STATE,
} from '../../models/auth.models';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthTokenResponsePassword } from '@supabase/supabase-js';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription, merge, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { ConfigurationService } from '../../../../shared/configuration/configuration.service';
import { SupabaseUserToolsService } from 'src/app/shared/services/supabase-user-tools.service';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { environment } from '../../../../../environments/environment';
import { ClubService } from '../../../../shared/services/club.service';

@Component({
  selector: 'app-auth-modal',
  standalone: false,
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss',
})
export class AuthComponent {
  /**
   * Google SSO is temporarily hidden while the OAuth client is under
   * Google's verification review. Flip to `true` to re-enable the
   * "Sign in with Google" buttons on the login and register screens
   * once the app is verified and the token-exchange issue is resolved.
   */
  readonly googleSsoEnabled = false;

  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  loading = false;
  public forgotPasswordVerification = false;
  isUserAdmin = false;
  isUserVerified = false;
  /** Terms of use (+ club rules, when published) accepted at sign up. */
  legalAccepted = false;
  private subscriptions: Subscription[] = [];
  private readonly clubService = inject(ClubService);

  constructor(
    private translateService: TranslationService,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private router: Router,
    private configurationService: ConfigurationService,
    private supabaseUserToolsService: SupabaseUserToolsService,
    private analytics: AnalyticsService,

    @Inject(MAT_DIALOG_DATA) public data: { method: AuthDialogMethod }
  ) {
    merge(this.credentials.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.loading = false;
        this.updateErrorMessage();
      });
  }
  readonly dialog = inject(MatDialogRef<AuthComponent>);
  public errorMessage$ = new BehaviorSubject('');

  get hasClubRules(): boolean {
    return !!this.clubService.theme()?.rules;
  }

  get canRegister(): boolean {
    return this.legalAccepted;
  }

  /**
   * Signup/login form. Register mode collects the full profile
   * (fullname, phone, birthday, gender) up front so the back-end
   * `store_new_user_with_profile` RPC writes a complete `users` row
   * in one shot — no more "ghost users" created by the old
   * "signup-then-fill-profile-later" flow where a dropped tab left
   * rows with NULL fullname/phone/birthday.
   *
   * Login and forgot-password modes ignore the extra fields.
   */
  credentials = this.formBuilder.group(
    {
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(30),
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
      ]),
      fullname: new FormControl('', [
        Validators.required,
        this.validNameValidator,
      ]),
      phone: new FormControl('', [
        Validators.required,
        Validators.pattern('[0-9 ]{10,13}'),
      ]),
      birthday: new FormControl<string | null>(null, [Validators.required]),
      gender: new FormControl('', [Validators.required]),
    },
    { validators: this.checkingPasswords }
  );

  /**
   * Matches the validator used on the existing profile-edit form so the
   * two surfaces agree on what a valid name looks like (allows Greek +
   * Latin letters, spaces, apostrophes, hyphens; rejects whitespace-only
   * and digits).
   */
  private validNameValidator(control: AbstractControl): ValidationErrors | null {
    const value = String(control.value ?? '').trim();
    if (value.length === 0) return { whitespace: true };
    const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿΆ-ώ\s'-]+$/;
    if (!nameRegex.test(value)) return { invalidName: true };
    return null;
  }

  /** Gender options — mirrors the profile-edit form. */
  readonly genders: { value: string; label: string }[] = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
  ];

  checkingPasswords(formGroup: AbstractControl): ValidationErrors | null {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    return password === confirmPassword ? null : { notMatched: true };
  }

  get passwordsMatch(): boolean {
    const password = this.credentials.get('password')?.value;
    const confirmPassword = this.credentials.get('confirmPassword')?.value;

    const match =
      password && confirmPassword && !this.credentials.hasError('notMatched');
    if (!match && password && confirmPassword) {
      this.errorMessage$.next('Password doesn’t match');
    } else {
      this.errorMessage$.next('');
    }
    return !this.credentials.hasError('notMatched');
  }

  get passwordValid() {
    return this.credentials.get('password')?.valid;
  }

  ngOnInit() {
    this.subscriptions.push(
      this.supabaseUserToolsService.isUserAdmin$.subscribe((isAdmin) => {
        this.isUserAdmin = isAdmin;
      })
    );

    this.subscriptions.push(
      this.supabaseUserToolsService.isUserVerified$.subscribe((isVerified) => {
        this.isUserVerified = isVerified;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  submitCredentials() {
    switch (this.data.method) {
      case AuthDialogMethod.LOGIN: {
        if (
          this.passwordValid &&
          this.credentials.value.email &&
          this.credentials.value.password
        ) {
          this.loading = true;
          this.subscriptions.push(this.userService
            .signIn(
              this.credentials.value.email.trim(),
              this.credentials.value.password.trim()
            )
            .subscribe((res: AuthTokenResponsePassword) => {
              if (res?.error) {
                this.userService.isLoggedIn.next(
                  AUTHORIZATION_STATE.NOT_LOGGED_IN
                );
                //this.errorMessage$.next(res.error.message);
                //this.updateErrorMessage();
                this.errorMessage$.next(
                  this.translateService.translate('Not valid credentials')
                );
                this.loading = false;
              } else {
                if (this.credentials.value.email && environment.enablePostHog) {
                  this.analytics.identifyUser(this.credentials.value.email, {
                    admin: this.isUserAdmin,
                  });
                }
                this.supabaseUserToolsService.updateUserStatus();
                this.router.navigate(['profile']).then(() => {
                  this.dialog.close(true);
                });
              }
            }));
        }
        break;
      }
      case AuthDialogMethod.REGISTER: {
        // Surface validation errors on every field the user hasn't
        // touched yet, so missing profile fields are obvious.
        this.credentials.markAllAsTouched();

        if (
          !this.credentials.valid ||
          !this.credentials.value.email ||
          !this.credentials.value.password ||
          !this.passwordsMatch ||
          !this.canRegister
        ) {
          return;
        }

        const form = this.credentials.value;
        this.loading = true;

        const user = this.userService.signupByEmail(
          form.email!.trim(),
          form.password!.trim(),
          {
            fullname: (form.fullname ?? '').trim(),
            phone: (form.phone ?? '').trim() || null,
            birthday: form.birthday ?? null,
            gender: form.gender ?? null,
          }
        );
        user.then((userData) => {
          if (userData.error) {
            this.updateErrorMessage();
            this.loading = false;
          } else {
            localStorage.setItem('email', form.email!);
            // Still route through /profile — the ProfileInfoPendingGuard
            // will see that the profile is already filled and bounce
            // the user straight to /calendar or /pendingVerification.
            // We don't hard-code either destination here; the guard is
            // the source of truth for "what should happen next".
            this.router.navigate(['profile']).then(() => {
              this.dialog.close(true);
            });
          }
        });
        user.catch((err) => {
          console.log(err.message);
          if (err.message === 'User already registered') {
            this.switchMethod(AuthDialogMethod.LOGIN);
          } else if (String(err.message).startsWith('SIGNUP_FAILED:')) {
            // signup-with-profile rolled back the auth row server-side
            // (or the reaper will within 30 min). UserService already
            // showed a snackbar; mirror it in the inline error too.
            this.errorMessage$.next(
              this.translateService.translate(
                'Could not complete signup. Please try again in a moment.'
              )
            );
          }
          this.loading = false;
        });
        break;
      }
      default: {
        throw new Error('Undefined auth method');
      }
    }
  }

  public switchMethod(method: AuthDialogMethod) {
    this.data.method = method;
  }

  async forgotPassword() {
    this.loading = true;
    const email = this.credentials.value.email;

    if (email)
      await this.userService.forgotPassword(email).then((res) => {
        this.loading = false;
        this.forgotPasswordVerification = true;
      });
    else console.error('missing email');
  }

  updateErrorMessage() {
    const emailErrors = this.credentials.controls.email?.errors;
    const passwordErrors = this.credentials.controls.password?.errors;

    if (emailErrors) {
      if (emailErrors['required']) {
        this.errorMessage$.next(
          this.translateService.translate('Missing Email')
        );
      }
    }
    if (
      passwordErrors &&
      !(this.data.method === AuthDialogMethod.FORGOT_PASSWORD)
    ) {
      if (passwordErrors['minlength']) {
        this.errorMessage$.next(
          this.translateService.translate(
            'Your password have to be at least 6 characters'
          )
        );
      }
      if (passwordErrors['required']) {
        this.errorMessage$.next(
          this.translateService.translate('Missing Password')
        );
      }
    }

    if (emailErrors && passwordErrors) {
      this.errorMessage$.next(
        this.translateService.translate('Not valid credentials')
      );
    }

    if (!emailErrors && !passwordErrors) {
      this.errorMessage$.next('');
    }
  }
  protected readonly AuthDialogMethod = AuthDialogMethod;

  async signInWithGoogle() {
    this.loading = true;
    const { error } = await this.userService.signInWithGoogle();
    if (error) {
      this.errorMessage$.next(
        this.translateService.translate('Google sign-in failed')
      );
      this.loading = false;
    }
    // If no error, Supabase redirects to Google — the dialog closes on redirect
  }

  closeDialog() {
    this.dialog.close();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
}
